import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight, ChevronDown, FolderOpen, Folder, Package, Plus, Copy, Trash2,
  Settings as SettingsIcon, ArrowLeft, Edit2, RefreshCw, AlertCircle
} from 'lucide-react';

  const api = axios.create({ withCredentials: true });
api.interceptors.request.use(config => {
  const token = localStorage.getItem('session_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function countItems(node) {
  if (!node.children) return 1;
  return node.children.reduce((sum, child) => sum + countItems(child), 0);
}

function TreeNode({ node, path, depth, onToggle, expanded, onEdit, onDelete }) {
  const { t } = useTranslation();
  const isFolder = "children" in node;
  const isExpanded = expanded[path];
  const indent = depth * 20;
  const itemCount = isFolder ? countItems(node) : 0;

  if (isFolder) {
    return (
      <div>
        <div
          className="flex items-center gap-2 py-1.5 px-2 hover:bg-zinc-800/50 cursor-pointer rounded transition-colors text-sm"
          style={{ marginLeft: indent }}
          onClick={() => onToggle(path)}
        >
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" /> :
            <ChevronRight className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />}
          {isExpanded ? <FolderOpen className="w-4 h-4 text-amber-400 flex-shrink-0" /> :
            <Folder className="w-4 h-4 text-amber-400 flex-shrink-0" />}
          <span className="text-zinc-200 truncate">{node.name}</span>
          <span className="text-xs text-zinc-500 ml-auto">{fmtNum(itemCount)}</span>
        </div>
        {isExpanded && node.children.map((child, i) => (
          <TreeNode key={i} node={child} path={`${path}.${i}`} depth={depth + 1}
            onToggle={onToggle} expanded={expanded}
            onEdit={onEdit} onDelete={onDelete} />
        ))}
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 py-1.5 px-2 hover:bg-zinc-800/50 rounded transition-colors text-sm group"
      style={{ marginLeft: indent }}
    >
      <Package className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" />
      <span className="text-zinc-200 truncate flex-1">{node.name}</span>
          {node.qty > 1 && (
        <span className="text-xs text-zinc-500 font-mono flex-shrink-0">x{node.qty}</span>
      )}
      <button onClick={e => { e.stopPropagation(); onEdit(path, node); }}
        className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-white transition-all flex-shrink-0">
        <Edit2 className="w-3 h-3" />
      </button>
      <button onClick={e => { e.stopPropagation(); onDelete(path); }}
        className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400 transition-all flex-shrink-0">
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

/** Числа с разделителем тысяч: 1 000 000 */
function fmtNum(n) {
  if (n == null || n === '') return '-';
  const num = Number(n);
  if (!Number.isFinite(num)) return '-';
  const isInt = Math.abs(num - Math.round(num)) < 1e-9;
  const str = isInt ? String(Math.round(num)) : num.toFixed(2);
  const [intPart, decPart] = str.split('.');
  const spaced = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
  return decPart != null ? `${spaced}.${decPart}` : spaced;
}

function fmtPct(pct) {
  if (pct == null || !Number.isFinite(pct)) return '';
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

function shortHubName(name, max = 14) {
  if (!name) return '—';
  return name.length > max ? name.slice(0, max) + '…' : name;
}

const stationKey = (id) => String(id);

function getStationData(stations, sid) {
  if (!stations || sid == null) return null;
  return stations[sid] ?? stations[stationKey(sid)] ?? null;
}

function CompareMarketCell({ sp, side, jitaSell, jitaBuy, jitaDir }) {
  if (!sp) return <span className="text-zinc-600">-</span>;
  const vol = side === 'sell' ? sp.sell_vol : sp.buy_vol;
  const price = side === 'sell' ? sp.sell : sp.buy;
  if (price == null) return <span className="text-zinc-600">-</span>;
  const jitaPrice = jitaDir === 'sell' ? jitaSell : jitaBuy;
  let pct = null;
  if (jitaPrice != null && jitaPrice > 0) {
    pct = (price - jitaPrice) / jitaPrice * 100;
  }
  const isPos = pct != null && pct >= 1;
  const isNeg = pct != null && pct <= -1;
  const pctFormatted = pct != null ? fmtPct(pct) : '';
  return (
    <span>
      <span className="text-zinc-400">({fmtNum(vol)})</span>
      <span className="text-zinc-300 ml-1">{fmtNum(price)}</span>
      {pctFormatted && (
        <span className={isPos ? 'text-green-400' : isNeg ? 'text-red-400' : 'text-zinc-500'}>
          {' '}{pctFormatted}
        </span>
      )}
    </span>
  );
}

function MyOrderCell({ my, t, side }) {
  if (!my || my.count <= 0) return <span className="text-zinc-700">—</span>;
  const isSell = side === 'sell';
  return (
    <span className="text-zinc-300/90 flex items-center gap-1.5 justify-end">
      <span className={isSell ? 'text-green-400/80' : 'text-blue-400/80'}>{isSell ? 'S' : 'B'}</span>
      <span className="text-zinc-300 font-mono">{fmtNum(my.price)}</span>
      {my.is_first
        ? <span className="inline-block w-2 h-2 rounded-full bg-green-500 shrink-0" title={t('trader.firstOrder')} />
        : <span className="inline-block w-2 h-2 rounded-full bg-red-500 shrink-0" title={t('trader.outbid')} />}
    </span>
  );
}

function itemHasMyOrders(item, visibleStations) {
  return visibleStations.some(st => {
    const sp = getStationData(item.stations, st.structure_id);
    return (sp?.my_sell?.count > 0) || (sp?.my_buy?.count > 0);
  });
}

export default function TraderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [selectedList, setSelectedList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pasteText, setPasteText] = useState('');
  const [parsedTree, setParsedTree] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editing, setEditing] = useState(null); // {type: 'new'|'edit', listId}
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [shoppingText, setShoppingText] = useState('');
  const [shoppingListLoading, setShoppingListLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [stationTabs, setStationTabs] = useState([]);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editItemModal, setEditItemModal] = useState(null);
  const [sortStation, setSortStation] = useState(null);
  const [sortMode, setSortMode] = useState(null); // 'sell' | 'buy'
  const [sortAsc, setSortAsc] = useState(true);
  const [jitaDir, setJitaDir] = useState('sell');
  const [hubDirs, setHubDirs] = useState({});
  const [parsing, setParsing] = useState(false);
  const [savingList, setSavingList] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type: 'success'|'error', msg: string}
  const showFeedback = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 3000);
  };

  const loadLists = useCallback(async () => {
    try {
      const res = await api.get('/api/trader/lists');
      setLists(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadLists(); }, [loadLists]);

  useEffect(() => {
    if (selectedListId && lists.length > 0) {
      const found = lists.find(l => l.id === selectedListId);
      setSelectedList(found || null);
    }
  }, [selectedListId, lists]);

  const handlePaste = async () => {
    if (!pasteText.trim()) return;
    setParsing(true);
    try {
      const res = await api.post('/api/trader/parse', { text: pasteText });
      setParsedTree(res.data.tree);
      showFeedback('success', `Разобрано ${res.data.tree.length} элементов`);
    } catch (e) {
      showFeedback('error', e.response?.data?.detail || 'Ошибка разбора списка');
      console.error(e);
    }
    setParsing(false);
  };

  const handleCreateList = async () => {
    if (!parsedTree) return;
    setSavingList(true);
    try {
      const res = await api.post('/api/trader/lists', { name: editingName || t('trader.newList'), tree: parsedTree });
      setEditing(null);
      setParsedTree(null);
      setPasteText('');
      setEditingName('');
      await loadLists();
      setSelectedListId(res.data.id);
      showFeedback('success', 'Список создан');
    } catch (e) {
      showFeedback('error', e.response?.data?.detail || 'Ошибка создания списка');
      console.error(e);
    }
    setSavingList(false);
  };

  const handleSaveList = async () => {
    if (!selectedList || !parsedTree) return;
    setSavingList(true);
    try {
      await api.put(`/api/trader/lists/${selectedList.id}`, { tree: parsedTree, name: editingName || selectedList.name });
      setEditing(null);
      setParsedTree(null);
      setPasteText('');
      setEditingName('');
      await loadLists();
      showFeedback('success', 'Список сохранен');
    } catch (e) {
      showFeedback('error', e.response?.data?.detail || 'Ошибка сохранения списка');
      console.error(e);
    }
    setSavingList(false);
  };

  const handleDeleteList = async (id) => {
    if (!window.confirm(t('trader.confirmDeleteMsg'))) return;
    try {
      await api.delete(`/api/trader/lists/${id}`);
      if (selectedListId === id) {
        setSelectedListId(null);
        setSelectedList(null);
        setCompareData(null);
      }
      await loadLists();
      showFeedback('success', 'Список удален');
    } catch (e) {
      showFeedback('error', 'Ошибка удаления списка');
      console.error(e);
    }
  };

  const handleRename = async (id, name) => {
    try {
      await api.put(`/api/trader/lists/${id}`, { name });
      await loadLists();
      showFeedback('success', 'Список переименован');
    } catch (e) {
      showFeedback('error', 'Ошибка переименования');
      console.error(e);
    }
  };

  const handleCompare = async (id) => {
    setCompareLoading(true);
    setCompareData(null);
    setCheckedItems({});
    setStationTabs([]);
    try {
      const compareRes = await api.get('/api/trader/compare', { params: { list_id: id } });
      setCompareData(compareRes.data);
      const stations = compareRes.data.stations || [];
      const firstId = stations[0]?.structure_id;
      setStationTabs(firstId != null ? [stationKey(firstId)] : []);
      setSortStation(firstId ?? null);
      setSortMode('sell');
      setSortAsc(true);
    } catch (e) { console.error(e); }
    setCompareLoading(false);
  };

  const handleShoppingList = (id) => {
    if (compareData) {
      const lines = [];
      compareData.items.forEach((it) => {
        const c = checkedItems[it.type_id];
        if (c?.checked) {
          lines.push(c.qty > 1 ? `${it.name} ${c.qty}` : it.name);
        }
      });
      setShoppingText(lines.length ? lines.join('\n') : t('trader.nothingFound'));
      return;
    }
    setShoppingListLoading(true);
    api.get(`/api/trader/shopping-list/${id}`).then(res => {
      setShoppingText(res.data.text);
    }).catch(e => {
      showFeedback('error', 'Ошибка загрузки списка покупок');
      console.error(e);
    }).finally(() => setShoppingListLoading(false));
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { console.error(e); }
  };

  const toggleExpand = (path) => {
    setExpanded(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const getNodeByPath = (tree, path) => {
    const parts = path.split('.').map(Number);
    let node = { children: tree };
    for (const p of parts) {
      if (node.children && node.children[p]) {
        node = node.children[p];
      } else return null;
    }
    return node;
  };

  const removeNodeByPath = (tree, path) => {
    const parts = path.split('.').map(Number);
    const parentIdx = parts.slice(0, -1);
    const last = parts[parts.length - 1];
    let parent = { children: tree };
    for (const p of parentIdx) {
      if (parent.children && parent.children[p]) {
        parent = parent.children[p];
      } else return;
    }
    if (parent.children && parent.children[last] !== undefined) {
      parent.children.splice(last, 1);
    }
  };

  const handleDeleteNode = (path) => {
    const newTree = JSON.parse(JSON.stringify(parsedTree || selectedList?.tree || []));
    removeNodeByPath(newTree, path);
    if (editing?.type === 'edit') {
      setParsedTree(newTree);
    } else if (editing?.type === 'new') {
      setParsedTree(newTree);
    }
  };

  const handleEditNode = (path, node) => {
    setEditItemModal({ path, name: node.name, qty: node.qty || 1, type_id: node.type_id });
  };

  const handleSaveEditItem = () => {
    if (!editItemModal) return;
    const newTree = JSON.parse(JSON.stringify(parsedTree || selectedList?.tree || []));
    const node = getNodeByPath(newTree, editItemModal.path);
    if (node) {
      node.name = editItemModal.name;
      node.qty = editItemModal.qty;
    }
    setParsedTree(newTree);
    setEditItemModal(null);
  };

  const renderTree = (treeNodes) => {
    if (!treeNodes || treeNodes.length === 0) return <div className="text-zinc-500 text-sm italic">{t('trader.nothingFound')}</div>;
    return treeNodes.map((node, i) => (
      <TreeNode key={i} node={node} path={`${i}`} depth={0}
        onToggle={toggleExpand} expanded={expanded}
        onEdit={handleEditNode} onDelete={handleDeleteNode} />
    ));
  };

  const renderCompareTable = () => {
    if (!compareData) return null;
    const { items, stations } = compareData;
    const visibleStations = stations.filter(s => stationTabs.map(stationKey).includes(stationKey(s.structure_id)));
    if (!items || items.length === 0) return <div className="text-zinc-500 text-sm italic">{t('trader.nothingFound')}</div>;

    const stationPct = (sid, item, mode) => {
      const sp = getStationData(item.stations, sid);
      if (!sp) return null;
      const price = mode === 'sell' ? sp.sell : sp.buy;
      const jitaPrice = jitaDir === 'sell' ? item.jita_sell : item.jita_buy;
      if (price == null || jitaPrice == null || jitaPrice <= 0) return null;
      return (price - jitaPrice) / jitaPrice * 100;
    };

    const sortedItems = [...items];
    if (sortStation != null && sortMode && stationTabs.map(stationKey).includes(stationKey(sortStation))) {
      sortedItems.sort((a, b) => {
        const aVal = stationPct(sortStation, a, sortMode);
        const bVal = stationPct(sortStation, b, sortMode);
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        return sortAsc ? aVal - bVal : bVal - aVal;
      });
    }

    const handleSort = (sid, mode) => {
      if (sortStation === sid && sortMode === mode) setSortAsc(!sortAsc);
      else { setSortStation(sid); setSortMode(mode); setSortAsc(true); }
    };

    const sortMark = (sid, mode) => (
      sortStation === sid && sortMode === mode ? (sortAsc ? ' ▲' : ' ▼') : ''
    );

    return (
      <div className="space-y-2 overflow-x-auto" style={{ maxWidth: '100%' }}>
        <table className="text-xs font-mono w-full" style={{ minWidth: Math.max(520, visibleStations.length * 180 + 180) }}>
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-800">
              <th className="py-2 pr-1 sticky left-0 z-10 w-8"></th>
              <th className="text-left py-2 pr-3 sticky left-8 z-10">{t('trader.item')}</th>
              <th className="text-right py-2 px-2">{t('trader.jita')}</th>
              {visibleStations.map(st => {
                const dir = hubDirs[st.structure_id] || 'sell';
                const label = `${jitaDir.toUpperCase()}>${dir.toUpperCase()}`;
                return (
                  <th key={st.structure_id}
                    className="text-right py-2 px-2 cursor-pointer hover:text-white transition-colors text-amber-400/90"
                    onClick={() => { handleSort(st.structure_id, dir); setHubDirs(prev => ({...prev, [st.structure_id]: dir})); }}
                    title={dir === 'sell' ? t('trader.sortBySell') : t('trader.sortByBuy')}
                  >
                    {shortHubName(st.name)} {label}{sortMark(st.structure_id, dir)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {(() => {
            const checkedFirst = [...sortedItems].filter(it => checkedItems[it.type_id]?.checked);
            const uncheckedRest = [...sortedItems].filter(it => !checkedItems[it.type_id]?.checked);
            const sortedChecked = [...checkedFirst, ...uncheckedRest];
            return sortedChecked.map((it) => {
              const hasMy = itemHasMyOrders(it, visibleStations);
              return (
              <React.Fragment key={it.type_id}>
              <tr className="border-b border-zinc-800/30 hover:bg-zinc-800/30">
                <td className="py-1.5 pr-1 sticky left-0 z-10">
                  <input type="checkbox"
                    checked={checkedItems[it.type_id]?.checked || false}
                    onChange={(e) => setCheckedItems(prev => ({
                      ...prev,
                      [it.type_id]: { checked: e.target.checked, qty: prev[it.type_id]?.qty || 1 }
                    }))}
                    className="w-3.5 h-3.5 accent-cyan-500 cursor-pointer" />
                </td>
                <td className="py-1.5 pr-3 sticky left-8 z-10 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <span className="text-zinc-200 truncate max-w-[188px] inline-block">{it.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(it.name); }}
                      className="text-zinc-600 hover:text-white transition-colors flex-shrink-0">
                      <Copy className="w-3 h-3" />
                    </button>
                    {checkedItems[it.type_id]?.checked && (
                      <input type="number" min="1"
                        value={checkedItems[it.type_id]?.qty || 1}
                        onChange={(e) => setCheckedItems(prev => ({
                          ...prev,
                          [it.type_id]: { checked: true, qty: parseInt(e.target.value) || 1 }
                        }))}
                        className="w-16 bg-zinc-950 border border-zinc-700 px-1.5 py-0.5 text-xs text-white font-mono outline-none focus:border-cyan-700 flex-shrink-0 ml-1" />
                    )}
                  </div>
                </td>
                <td className="text-right py-1.5 px-2 text-zinc-300">
                  {jitaDir === 'sell' ? fmtNum(it.jita_sell) : fmtNum(it.jita_buy)}
                </td>
                {visibleStations.map(st => {
                  const sp = getStationData(it.stations, st.structure_id);
                  const dir = hubDirs[st.structure_id] || 'sell';
                  return (
                    <td key={st.structure_id} className="text-right py-1.5 px-2 align-top">
                      <CompareMarketCell sp={sp} side={dir} jitaSell={it.jita_sell} jitaBuy={it.jita_buy} jitaDir={jitaDir} />
                    </td>
                  );
                })}
              </tr>
              {hasMy && (
              <tr className="border-b border-zinc-800/50 bg-zinc-900/40">
                <td className="py-1 pr-1 sticky left-0 z-10" />
                <td className="py-1 pr-3 sticky left-8 z-10 text-[10px] text-zinc-500 pl-1">
                  {t('trader.myOrders')}
                </td>
                <td className="py-1 px-2" />
                {visibleStations.map(st => {
                  const sp = getStationData(it.stations, st.structure_id);
                  const dir = hubDirs[st.structure_id] || 'sell';
                  const myKey = dir === 'sell' ? sp?.my_sell : sp?.my_buy;
                  return (
                    <td key={`my-${st.structure_id}`} className="text-right py-1 px-2 whitespace-nowrap">
                      <MyOrderCell my={myKey} t={t} side={dir} />
                    </td>
                  );
                })}
              </tr>
              )}
              </React.Fragment>
              );
            });
            })()}
            </tbody>
          </table>
          <button onClick={() => setCompareData(null)}
            className="text-xs text-zinc-500 hover:text-white transition-colors mt-2">
            ← {t('common.back')}
          </button>
        </div>
    );
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/dashboard')}
            className="text-zinc-500 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-light tracking-tight text-white uppercase flex-1">
            {t('trader.trader')}
          </h1>
          <button onClick={() => navigate('/settings?tab=trader')}
            className="text-zinc-500 hover:text-white transition-colors flex items-center gap-1 text-sm">
            <SettingsIcon className="w-4 h-4" />
            {t('trader.traderSettings')}
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Sidebar - Lists */}
          <div className="xl:col-span-1 space-y-4">
            <div className="bg-surface border border-border p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs font-mono uppercase tracking-wider text-zinc-400">{t('trader.newList')}</h2>
                <button onClick={() => { setEditing({ type: 'new' }); setParsedTree(null); setPasteText(''); setEditingName(''); }}
                  className="text-cyan-400 hover:text-cyan-300 transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {lists.length === 0 ? (
                <div className="text-xs text-zinc-500 italic">{t('trader.noLists')}</div>
              ) : (
                <div className="space-y-1 max-h-80 overflow-y-auto">
                  {lists.map(l => (
                    <div key={l.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer transition-colors group ${selectedListId === l.id ? 'bg-cyan-900/30 text-cyan-300' : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200'}`}
                      onClick={() => { setSelectedListId(l.id); setEditing(null); setCompareData(null); }}>
                      <span className="truncate flex-1">{l.name}</span>
                      <button onClick={e => { e.stopPropagation(); const newName = prompt(t('trader.rename'), l.name); if (newName) handleRename(l.id, newName); }}
                        className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-white transition-all">
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleDeleteList(l.id); }}
                        className="text-zinc-600 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions for selected list */}
            {selectedList && !editing && (
              <div className="bg-surface border border-border p-4 rounded-lg space-y-2">
                <button onClick={() => handleCompare(selectedList.id)} disabled={compareLoading}
                  className="w-full bg-cyan-700 hover:bg-cyan-600 text-white text-sm py-2 rounded transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                  {compareLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                  {t('trader.compare')}
                </button>
                <button onClick={() => handleShoppingList(selectedList.id)} disabled={shoppingListLoading}
                  className="w-full border border-zinc-600 text-zinc-300 text-sm py-2 rounded hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                  {shoppingListLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                  {t('trader.shoppingList')}
                </button>
                <button onClick={() => { setEditing({ type: 'edit', listId: selectedList.id }); setParsedTree(selectedList.tree); setPasteText(''); setEditingName(selectedList.name); }}
                  className="w-full border border-zinc-600 text-zinc-300 text-sm py-2 rounded hover:bg-zinc-800 transition-colors">
                  <Edit2 className="w-4 h-4 inline mr-1" />
                  {t('trader.rename')}
                </button>
                <button onClick={() => handleDeleteList(selectedList.id)}
                  className="w-full border border-red-900/50 text-red-400 text-sm py-2 rounded hover:bg-red-950/30 transition-colors">
                  <Trash2 className="w-4 h-4 inline mr-1" />
                  {t('trader.delete')}
                </button>
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="xl:col-span-3 space-y-6">
            {/* List editor */}
            {(editing?.type === 'new' || editing?.type === 'edit') && (
              <div className="bg-surface border border-border p-4 rounded-lg">
                <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-400 mb-3">
                  {editing.type === 'new' ? t('trader.newList') : t('trader.rename')}
                </h2>
                <p className="text-xs text-zinc-500 mb-3">{t('trader.pasteDesc')}</p>
                <div className="space-y-4">
                  <textarea
                    className="w-full bg-zinc-950 border border-border p-4 text-white font-mono text-sm focus:border-primary outline-none transition-colors"
                    rows="5"
                    value={pasteText}
                    onChange={e => setPasteText(e.target.value)}
                    placeholder="+ Ships\n---\n---Tritanium 1000\n+ Ammo\n---Antimatter Charge M 500"
                  />
                  <div className="flex gap-2">
                    <button onClick={handlePaste} disabled={parsing}
                      className="bg-primary text-black px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50">
                      {parsing ? '...' : t('trader.create')}
                    </button>
                    <button onClick={() => { setEditing(null); setParsedTree(null); setPasteText(''); }}
                      className="border border-zinc-600 text-zinc-300 px-4 py-2 text-sm hover:bg-zinc-800 transition-colors">
                      {t('common.cancel')}
                    </button>
                  </div>
                  {parsedTree && (
                    <div className="bg-zinc-900/50 border border-border rounded p-3">
                      <input className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-white font-mono mb-2 outline-none focus:border-cyan-700"
                        value={editingName} onChange={e => setEditingName(e.target.value)}
                        placeholder={t('trader.listName')} />
                      <div className="text-xs text-zinc-500 mb-2">{fmtNum(parsedTree.length)} {t('trader.qty')}</div>
                      <div className="max-h-60 overflow-y-auto">
                        {renderTree(parsedTree)}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button onClick={editing.type === 'edit' ? handleSaveList : handleCreateList} disabled={savingList}
                          className="bg-cyan-700 text-white px-4 py-2 text-sm font-medium hover:bg-cyan-600 transition-colors disabled:opacity-50">
                          {savingList ? '...' : t('common.save')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Compare view */}
            {compareData && (
              <div className="bg-surface border border-border p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-400">
                    {t('trader.compareTitle')}
                  </h2>
                  <span className="text-xs text-zinc-500">{selectedList?.name}</span>
                </div>
                {compareData.stations && compareData.stations.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    <div className="flex items-center gap-0.5 mr-2">
                      <span className="text-[10px] text-zinc-500 mr-1">Jita</span>
                      <button onClick={() => setJitaDir('sell')}
                        className={`text-[10px] px-1.5 py-0.5 leading-none rounded transition-colors ${jitaDir === 'sell' ? 'bg-amber-600 text-white' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>S</button>
                      <button onClick={() => setJitaDir('buy')}
                        className={`text-[10px] px-1.5 py-0.5 leading-none rounded transition-colors ${jitaDir === 'buy' ? 'bg-cyan-600 text-white' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>B</button>
                    </div>
                    {compareData.stations.map(st => {
                      const active = stationTabs.map(stationKey).includes(stationKey(st.structure_id));
                      const dir = hubDirs[st.structure_id] || 'sell';
                      return (
                        <div key={st.structure_id} className="flex items-center gap-0.5">
                          <button
                            onClick={() => {
                              setStationTabs([stationKey(st.structure_id)]);
                              setSortStation(st.structure_id);
                              if (!sortMode) setSortMode(dir);
                            }}
                            className={`text-xs px-2 py-1 rounded-l transition-colors ${active ? 'bg-cyan-700 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200'}`}>
                            {st.name}
                          </button>
                          <div className="flex flex-col">
                            <button
                              onClick={() => {
                                setHubDirs(prev => ({...prev, [st.structure_id]: 'sell'}));
                                if (active && sortMode !== 'sell') setSortMode('sell');
                              }}
                              className={`text-[10px] px-1 py-0 leading-none transition-colors ${dir === 'sell' ? 'bg-amber-600 text-white' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>S</button>
                            <button
                              onClick={() => {
                                setHubDirs(prev => ({...prev, [st.structure_id]: 'buy'}));
                                if (active && sortMode !== 'buy') setSortMode('buy');
                              }}
                              className={`text-[10px] px-1 py-0 leading-none transition-colors ${dir === 'buy' ? 'bg-cyan-600 text-white' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>B</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {compareData.items && compareData.items.length > 0 && (() => {
                  const total = compareData.items.length;
                  const checked = Object.values(checkedItems).filter(c => c.checked).length;
                  const qtySum = Object.entries(checkedItems).reduce((s, [, c]) => {
                    return c.checked ? s + (c.qty || 1) : s;
                  }, 0);
                  return (
                    <div className="flex items-center gap-3 text-xs text-zinc-500 mb-3 px-1">
                      <span>Выбрано: <span className="text-zinc-200">{checked}</span> / {total}</span>
                      {checked > 0 && <span>Кол-во: <span className="text-zinc-200">{fmtNum(qtySum)}</span></span>}
                    </div>
                  );
                })()}
                {renderCompareTable()}
              </div>
            )}

            {/* Shopping list modal */}
            {shoppingText && (
              <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => setShoppingText('')}>
                <div className="bg-surface border border-border rounded-lg p-4 w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-400">
                      {t('trader.shoppingList')}
                    </h2>
                    <button onClick={() => copyToClipboard(shoppingText)}
                      className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1">
                      {copied ? '✓' : <Copy className="w-3 h-3" />}
                      {copied ? 'Copied!' : t('trader.copy')}
                    </button>
                  </div>
                  <pre className="bg-zinc-950 border border-border p-3 text-xs text-zinc-300 font-mono whitespace-pre-wrap flex-1 overflow-y-auto">
                    {shoppingText}
                  </pre>
                  <button onClick={() => setShoppingText('')}
                    className="text-xs text-zinc-500 hover:text-white transition-colors mt-2 self-start">
                    ← {t('common.back')}
                  </button>
                </div>
              </div>
            )}

            {/* Selected list tree view */}
            {selectedList && !editing && !compareData && (
              <div className="bg-surface border border-border p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-400"
                    onClick={() => { const newName = prompt(t('trader.rename'), selectedList.name); if (newName) handleRename(selectedList.id, newName); }}>
                    {selectedList.name}
                    <Edit2 className="w-3 h-3 inline ml-1 text-zinc-600 hover:text-white cursor-pointer" />
                  </h2>
                  <span className="text-xs text-zinc-500">
                    {t('trader.updatedAt')}: {selectedList.updated_at ? new Date(selectedList.updated_at).toLocaleDateString() : '-'}
                  </span>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {renderTree(selectedList.tree || [])}
                </div>
                {(!selectedList.tree || selectedList.tree.length === 0) && (
                  <div className="text-zinc-500 text-sm italic">{t('trader.nothingFound')}</div>
                )}
              </div>
            )}

            {/* No list selected */}
            {!selectedList && !editing && !compareData && (
              <div className="bg-surface border border-border p-8 rounded-lg text-center">
                <Package className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <div className="text-zinc-500 text-sm">{t('trader.selectList')}</div>
                <button onClick={() => { setEditing({ type: 'new' }); setParsedTree(null); setPasteText(''); setEditingName(''); }}
                  className="mt-4 bg-cyan-700 hover:bg-cyan-600 text-white px-4 py-2 text-sm rounded transition-colors">
                  <Plus className="w-4 h-4 inline mr-1" />
                  {t('trader.newList')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Feedback toast */}
        {feedback && (
          <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-2xl text-sm font-mono z-50 transition-all animate-fadeIn ${feedback.type === 'success' ? 'bg-green-900 text-green-100 border border-green-700' : 'bg-red-900 text-red-100 border border-red-700'}`}>
            {feedback.msg}
          </div>
        )}

        {/* Edit item modal */}
        {editItemModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setEditItemModal(null)}>
            <div className="bg-surface border border-border p-6 rounded-lg w-96 max-w-full" onClick={e => e.stopPropagation()}>
              <h3 className="text-sm font-mono uppercase tracking-wider text-zinc-400 mb-4">{t('trader.editItem')}</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">{t('trader.itemName')}</label>
                  <input className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-white font-mono outline-none focus:border-cyan-700"
                    value={editItemModal.name} onChange={e => setEditItemModal({ ...editItemModal, name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">{t('trader.quantity')}</label>
                  <input type="number" min="1" className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-white font-mono outline-none focus:border-cyan-700"
                    value={editItemModal.qty} onChange={e => setEditItemModal({ ...editItemModal, qty: parseInt(e.target.value) || 1 })} />
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={handleSaveEditItem}
                    className="bg-cyan-700 text-white px-4 py-2 text-sm hover:bg-cyan-600 transition-colors">
                    {t('common.save')}
                  </button>
                  <button onClick={() => setEditItemModal(null)}
                    className="border border-zinc-600 text-zinc-300 px-4 py-2 text-sm hover:bg-zinc-800 transition-colors">
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LoadingScreen() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="font-mono text-primary text-xl animate-pulse uppercase">
        {t('system.initializing')}
      </div>
    </div>
  );
}
